<script setup lang="ts">
import { ref, onBeforeMount, watch } from 'vue';
import router from '@/router';
import { RouterView } from 'vue-router';
import NavBar from '@/components/NavBar.vue';
import { setup as i18nextSetup } from '@/lib/util/i18n';
import defaultTranslationKeys from '@/lib/util/i18n/default-translation-keys';
import i18n from 'i18next';
import {
  startCodeVersionAutoUpdateCheck,
  scheduleNightlyReload,
} from '@/lib/util/codeSync';
import { startDataSyncCheck } from '@/lib/util/dataSync';
import { useRootStore } from '@/stores/root';

import {
  setCallbackAfterInactivity,
  clearInactivityEvents,
} from '@/lib/util/inactivity';
import { useUserStore } from '@/stores/user';
import { useEmployeeWithTimesheetsStore } from '@/stores/employeesWithTimesheets/employeesWithTimesheets';
import { setOnTheMinuteInterval } from '@/lib/util/dateTime';
import { useKioskConfigStore } from '@/stores/kioskConfig';
import { getTranslations } from '@/lib/util/i18n';
import { reloadAnalyticsService } from '@/lib/util/analytics/reloadAnalyticsService';

const rootStore = useRootStore();
const user = useUserStore();
const employeeWithTimesheetsStore = useEmployeeWithTimesheetsStore();
const kioskConfigStore = useKioskConfigStore();
const isReady = ref(false);

const t = getTranslations({
  app: '',
  module: 'views',
  file: 'app',
});

watch(
  () => router.currentRoute.value,
  () => {
    clearInactivityEvents();
    if (router.currentRoute.value.name == 'me') {
      setCallbackAfterInactivity(60000, () => {
        user.logout();
        router.push({ name: 'home' });
      });
    } else if (router.currentRoute.value.name == 'login') {
      setCallbackAfterInactivity(20000, () => router.push({ name: 'home' }));
    }
  }
);

onBeforeMount(async () => {
  if (!rootStore.canInitialize()) {
    window.location.assign('/exec/config/kiosks?-create=true');
    return;
  }

  reloadAnalyticsService();
  const isLoadingRootStore = rootStore.load();
  await i18nextSetup();

  startCodeVersionAutoUpdateCheck(15);
  scheduleNightlyReload(4);
  startDataSyncCheck(15, 5);
  setOnTheMinuteInterval(() => {
    employeeWithTimesheetsStore.computeEmployeeGroups();
  });

  isReady.value = true;
  await isLoadingRootStore;

  const kioskBusinessSettings = kioskConfigStore?.data?.KioskBusinessSettings;
  i18n.options.interpolation!.defaultVariables =
    defaultTranslationKeys(kioskBusinessSettings) || {};

  rootStore.completeI18nextInitialization();

  document.title = `${kioskConfigStore.data.KioskName} - ${t(
    'document-title'
  )}`;
});
</script>

<template>
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <div v-if="isReady">
    <NavBar
      :companyName="
        kioskConfigStore.data.KioskAppConfigs?.ENABLE_MULTI_LOCATIONS
          ? `${t('all-locations')}`
          : kioskConfigStore.data.KioskCompanyNameRaw
      "
    />
    <main>
      <RouterView />
    </main>
  </div>
</template>

<style>
@import '@/assets/styles/index.css';

main {
  display: flex;
  justify-content: center;
  margin-top: 7rem;
}

@media (max-width: 1200px) {
  main {
    margin: 0;
  }

  .co-dialog {
    width: 96% !important;
    margin: 2% !important;
    box-shadow: none !important;
  }

  .co-overlay {
    background-color: white;
  }
}
</style>
